import { FunctionComponent, useMemo } from "react";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { classNames } from "@/utils/classNames";

import { ContentStatus, SegmentStatusFilter } from "../schema/knowledgeDb";
import { Objection, ObjectionCategory } from "../schema/objectionHandling";
import { getOverallStatusFromList } from "../utils/transformers";

interface ObjectionCategorySidebarProps {
	categories: ObjectionCategory[];
	objections: Objection[];
	activeCategoryId: number | null;
	statusFilter: SegmentStatusFilter;
	selectedObjectionIds: Set<number>;
	onCategorySelect: (categoryId: number) => void;
	onAddCategory: () => void;
	onDeleteCategory: (categoryId: number) => void;
	onStatusFilterChange: (filter: SegmentStatusFilter) => void;
	onToggleObjectionSelection: (objectionId: number) => void;
	onSelectAll: (objectionIds: number[]) => void;
	onBulkApprove: () => void;
}

export const ObjectionCategorySidebar: FunctionComponent<ObjectionCategorySidebarProps> = ({
	categories,
	objections,
	activeCategoryId,
	statusFilter,
	selectedObjectionIds,
	onCategorySelect,
	onAddCategory,
	onDeleteCategory,
	onStatusFilterChange,
	onToggleObjectionSelection,
	onSelectAll,
	onBulkApprove,
}) => {
	const categoryMeta = useMemo(() => {
		const map = new Map<number, { count: number; objIds: number[]; statuses: ContentStatus[] }>();

		for (const cat of categories) {
			map.set(cat.id, { count: 0, objIds: [], statuses: [] });
		}

		for (const obj of objections) {
			const entry = map.get(obj.categoryId);

			if (entry) {
				entry.count += 1;
				entry.objIds.push(obj.id);
				entry.statuses.push(obj.status);
			}
		}

		return map;
	}, [categories, objections]);

	const filteredCategories = useMemo(() => {
		if (statusFilter === SegmentStatusFilter.ALL) {
			return categories;
		}

		return categories.filter((cat) => {
			const meta = categoryMeta.get(cat.id);

			if (!meta || meta.statuses.length === 0) {
				return false;
			}

			const overall = getOverallStatusFromList(meta.statuses);

			if (statusFilter === SegmentStatusFilter.AI_DRAFTED) {
				return overall === ContentStatus.AI_DRAFTED;
			}

			return overall === ContentStatus.HUMAN_APPROVED;
		});
	}, [categories, categoryMeta, statusFilter]);

	const handleToggleCategory = (catId: number) => {
		const meta = categoryMeta.get(catId);

		if (meta) {
			for (const id of meta.objIds) {
				onToggleObjectionSelection(id);
			}
		}
	};

	const isCategorySelected = (catId: number) => {
		const meta = categoryMeta.get(catId);

		if (!meta || meta.objIds.length === 0) {
			return false;
		}

		return meta.objIds.every((id) => selectedObjectionIds.has(id));
	};

	const isBulkMode = selectedObjectionIds.size > 0;
	const visibleObjectionIds = filteredCategories.flatMap(
		(cat) => categoryMeta.get(cat.id)?.objIds ?? []
	);
	const allSelected =
		visibleObjectionIds.length > 0 &&
		visibleObjectionIds.every((id) => selectedObjectionIds.has(id));
	const someSelected = visibleObjectionIds.some((id) => selectedObjectionIds.has(id));

	return (
		<div className="w-[280px] min-w-[280px] border-r border-gray-200 bg-brand-white flex flex-col sticky top-0 h-full overflow-y-auto">
			{/* Header */}
			<div className="px-4 py-3 border-b border-gray-200">
				<h3 className="text-sm font-semibold text-gray-900">Objection Categories</h3>
				<p className="text-xs text-gray-500 mt-1">
					{objections.length} objection{objections.length === 1 ? "" : "s"} total
				</p>
			</div>

			{/* Status Filter */}
			<div className="px-4 py-2 border-b border-gray-200">
				<select
					className="w-full text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
					value={statusFilter}
					onChange={(e) => onStatusFilterChange(e.target.value as SegmentStatusFilter)}
				>
					<option value={SegmentStatusFilter.ALL}>All Objections</option>
					<option value={SegmentStatusFilter.AI_DRAFTED}>AI-drafted only</option>
					<option value={SegmentStatusFilter.APPROVED}>Approved only</option>
				</select>
			</div>

			{/* Select all */}
			{visibleObjectionIds.length > 0 && (
				<div className="px-4 py-2 border-b border-gray-200">
					<Checkbox
						isChecked={allSelected}
						isIndeterminate={someSelected && !allSelected}
						onChange={() => onSelectAll(allSelected ? [] : visibleObjectionIds)}
					>
						<span className="text-xs font-medium text-gray-600">
							Select all ({visibleObjectionIds.length})
						</span>
					</Checkbox>
				</div>
			)}

			{/* Category list */}
			<div className="flex-1 overflow-y-auto py-2">
				{filteredCategories.map((cat) => {
					const meta = categoryMeta.get(cat.id);
					const count = meta?.count ?? 0;
					const overallStatus = meta ? getOverallStatusFromList(meta.statuses) : null;

					return (
						<div
							key={cat.id}
							className={classNames(
								"group flex items-center w-full px-4 py-2.5 text-left text-sm transition-colors border-l-2",
								activeCategoryId === cat.id
									? "bg-brand-light/10 text-brand-light font-medium border-brand-light"
									: "text-gray-700 hover:bg-gray-200/50 border-transparent"
							)}
						>
							{/* Bulk selection checkbox */}
							<div className="mr-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
								<Checkbox
									isChecked={isCategorySelected(cat.id)}
									onChange={() => handleToggleCategory(cat.id)}
								/>
							</div>
							<button
								className="flex-1 truncate text-left cursor-pointer"
								type="button"
								onClick={() => onCategorySelect(cat.id)}
							>
								{cat.name}
							</button>
							<div className="flex items-center gap-1.5 shrink-0 ml-2">
								<button
									className="hidden group-hover:inline-flex items-center justify-center w-5 h-5 text-[10px] text-red-500 hover:text-red-500/80 hover:bg-red-50 rounded transition-colors cursor-pointer"
									title="Delete category"
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										onDeleteCategory(cat.id);
									}}
								>
									✕
								</button>
								{overallStatus === ContentStatus.AI_DRAFTED && (
									<span className="text-[10px]">✨</span>
								)}
								{overallStatus === ContentStatus.HUMAN_APPROVED && (
									<span className="text-[10px] text-status-success">✓</span>
								)}
								<span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-semibold rounded-full bg-gray-200 text-gray-600">
									{count}
								</span>
							</div>
						</div>
					);
				})}
			</div>

			{/* Bulk approve action bar */}
			{isBulkMode && (
				<div className="sticky bottom-0 px-4 py-3 border-t border-gray-200 bg-white">
					<button
						className="w-full px-3 py-2 text-xs font-medium text-white bg-status-warning rounded-lg hover:bg-status-warning/90 transition-colors"
						type="button"
						onClick={onBulkApprove}
					>
						✓ Approve Selected ({selectedObjectionIds.size})
					</button>
				</div>
			)}

			{/* Add button */}
			<div className="px-4 py-3 border-t border-gray-200">
				<button
					className="flex items-center justify-center w-full px-3 py-2 text-sm font-medium text-brand-light bg-brand-light/10 rounded-lg hover:bg-brand-light/20 transition-colors cursor-pointer"
					type="button"
					onClick={onAddCategory}
				>
					+ Add Custom Category
				</button>
			</div>
		</div>
	);
};
